/**
 * Carousel view script (ES module).
 *
 * This file is intended for frontend behavior only.
 * If WordPress ever enqueues it in the Site Editor iframe, bail to avoid DOM side effects.
 */
const isEditorIframe = ( () => {
	try {
		return (
			document?.body?.classList?.contains(
				"block-editor-iframe__body"
			) ?? false
		);
	} catch ( e ) {
		return false;
	}
} )();

if ( ! isEditorIframe ) {
	function clamp( n, min, max ) {
		return Math.max( min, Math.min( max, n ) );
	}

	function wrapIndex( index, length ) {
		if ( length <= 0 ) {
			return 0;
		}
		const mod = index % length;
		return mod < 0 ? mod + length : mod;
	}

	const CLONE_ATTR = "data-rlcship-clone";
	const ORIGIN_ATTR = "data-rlcship-origin-index";

	function isCloneSlide( node ) {
		return (
			!! node &&
			node.nodeType === 1 &&
			typeof node.getAttribute === "function" &&
			node.getAttribute( CLONE_ATTR ) === "1"
		);
	}

	function stripCloneAttrsRecursive( node ) {
		if ( ! node || node.nodeType !== 1 ) {
			return;
		}
		try {
			if ( typeof node.removeAttribute === "function" ) {
				node.removeAttribute( "id" );
				// Strip srcset/sizes from images to prevent duplicate large image loads.
				// Clones are decorative, so a single src is sufficient.
				if ( node.tagName === "IMG" ) {
					node.removeAttribute( "srcset" );
					node.removeAttribute( "sizes" );
					node.removeAttribute( "fetchpriority" );
					node.setAttribute( "loading", "lazy" );
					node.setAttribute( "decoding", "async" );
				}
			}
		} catch ( e ) {
			// ignore
		}
		const kids = node.children;
		if ( ! kids || ! kids.length ) {
			return;
		}
		for ( let i = 0; i < kids.length; i++ ) {
			stripCloneAttrsRecursive( kids[ i ] );
		}
	}

	function removeExistingClones( container ) {
		if ( ! container || ! container.children ) {
			return;
		}
		for ( let i = container.children.length - 1; i >= 0; i-- ) {
			const node = container.children[ i ];
			if ( isCloneSlide( node ) && node.parentNode ) {
				node.parentNode.removeChild( node );
			}
		}
	}

	function computeCloneCount( track, originals ) {
		if ( ! track || ! originals || originals.length < 2 ) {
			return 1;
		}
		const trackRect = track.getBoundingClientRect();
		const trackWidth = trackRect.width || track.clientWidth || 0;
		const slideRect = originals[ 0 ].getBoundingClientRect();
		const slideWidth = slideRect.width || originals[ 0 ].clientWidth || 0;
		if ( ! trackWidth || ! slideWidth ) {
			return 1;
		}
		const estimatedVisible = Math.ceil( trackWidth / slideWidth );
		return clamp( estimatedVisible, 1, originals.length );
	}

	function isEditorLayout( node ) {
		return (
			!! node &&
			node.classList &&
			node.classList.contains( "block-editor-block-list__layout" )
		);
	}

	function getSlideContainer( track ) {
		if ( ! track ) {
			return null;
		}
		const first = track.firstElementChild;
		if ( ! first ) {
			return track;
		}
		if (
			first.classList &&
			first.classList.contains( "block-editor-inner-blocks" )
		) {
			const nested = first.querySelector(
				".block-editor-block-list__layout"
			);
			return nested || first;
		}
		return isEditorLayout( first ) ? first : track;
	}

	function getSlideElements( track ) {
		const container = getSlideContainer( track );
		if ( ! container || ! container.children ) {
			return [];
		}
		const nodes = container.children;
		const slides = [];
		for ( let i = 0; i < nodes.length; i++ ) {
			const node = nodes[ i ];
			if ( ! node || node.nodeType !== 1 ) {
				continue;
			}
			if (
				node.classList &&
				node.classList.contains( "block-list-appender" )
			) {
				continue;
			}
			slides.push( node );
		}
		return slides;
	}

	function getPaddingStart( track ) {
		if ( ! track || typeof window.getComputedStyle !== "function" ) {
			return 0;
		}
		const styles = window.getComputedStyle( track );
		const padding = styles ? parseFloat( styles.paddingLeft || "0" ) : 0;
		if ( typeof Number.isFinite === "function" ) {
			return Number.isFinite( padding ) ? padding : 0;
		}
		return isFinite( padding ) ? padding : 0;
	}

	function computeSlideTargets( track, slides, align ) {
		const targets = [];
		if ( ! track || ! slides || ! slides.length ) {
			return targets;
		}
		const trackRect = track.getBoundingClientRect();
		const paddingLeft = getPaddingStart( track );
		const trackWidth = trackRect.width || track.clientWidth || 0;
		for ( let i = 0; i < slides.length; i++ ) {
			const rect = slides[ i ].getBoundingClientRect();
			let left =
				rect.left - trackRect.left + track.scrollLeft - paddingLeft;
			if ( align === "center" && trackWidth > 0 ) {
				left = left + rect.width / 2 - trackWidth / 2;
			}
			if ( left < 0 ) {
				left = 0;
			}
			targets.push( left );
		}
		return targets;
	}

	function getNearestIndex( scrollLeft, targets ) {
		if ( ! targets || ! targets.length ) {
			return 0;
		}
		let nearest = 0;
		let minDelta = Math.abs( scrollLeft - targets[ 0 ] );
		for ( let i = 1; i < targets.length; i++ ) {
			const delta = Math.abs( scrollLeft - targets[ i ] );
			if ( delta < minDelta ) {
				minDelta = delta;
				nearest = i;
			}
		}
		return nearest;
	}

	/**
	 * Temporarily disable CSS scroll-snap and scroll-behavior on the track,
	 * perform a synchronous scroll, then restore both after a rAF + microtask.
	 * This prevents the browser from animating "instant" jumps (e.g. the
	 * clone-to-real correction) as a visible rewind across all intermediate
	 * slides.
	 */
	function instantScrollTo( track, left ) {
		if ( ! track || typeof left !== 'number' || ! isFinite( left ) ) {
			return;
		}
		const prevSnap = track.style.scrollSnapType;
		const prevBehavior = track.style.scrollBehavior;
		track.style.scrollSnapType = 'none';
		track.style.scrollBehavior = 'auto';
		try {
			track.scrollLeft = left;
		} catch ( e ) {
			// ignore
		}
		// Restore after the browser has applied the position change.
		window.requestAnimationFrame( function () {
			track.style.scrollSnapType = prevSnap;
			track.style.scrollBehavior = prevBehavior;
		} );
	}

	function scrollToIndex( track, index, targets, slides, behavior ) {
		if ( ! track || typeof index !== "number" ) {
			return;
		}
		const finalTargets =
			targets && targets.length
				? targets
				: computeSlideTargets( track, slides );
		const left = finalTargets[ index ];
		if ( typeof left !== "number" || ! isFinite( left ) ) {
			return;
		}
		// For instant/auto corrections, bypass snap + smooth to prevent rewind.
		if ( behavior === 'instant' || behavior === 'auto' ) {
			instantScrollTo( track, left );
			return;
		}
		const options = { left };
		options.behavior = behavior || "smooth";
		try {
			track.scrollTo( options );
		} catch ( err ) {
			track.scrollLeft = left;
		}
	}

	function parseBool( v, fallback ) {
		if ( v === null || v === undefined ) {
			return !! fallback;
		}
		return v === "true" || v === "1";
	}

	function parseIntSafe( v, fallback ) {
		const n = parseInt( v, 10 );
		return isNaN( n ) ? fallback : n;
	}

	function ensureDots( root ) {
		const existing = root.querySelector( ".rlcship-carousel__dots" );
		if ( existing ) {
			return existing;
		}
		const el = document.createElement( "div" );
		el.className = "rlcship-carousel__dots";
		root.appendChild( el );
		return el;
	}

	function renderDots( dotsEl, count ) {
		while ( dotsEl.firstChild ) {
			dotsEl.removeChild( dotsEl.firstChild );
		}
		for ( let i = 0; i < count; i++ ) {
			const b = document.createElement( "button" );
			b.type = "button";
			b.className = "rlcship-carousel__dot";
			b.setAttribute( "aria-label", "Go to slide " + ( i + 1 ) );
			b.setAttribute( "data-index", String( i ) );
			dotsEl.appendChild( b );
		}
	}

	function setActiveDot( dotsEl, index ) {
		if ( ! dotsEl ) {
			return;
		}
		const dots = dotsEl.querySelectorAll( ".rlcship-carousel__dot" );
		for ( let i = 0; i < dots.length; i++ ) {
			const active = i === index;
			dots[ i ].classList.toggle( "is-active", active );
			dots[ i ].setAttribute( "aria-current", active ? "true" : "false" );
		}
	}

	function initOrUpdateCarousel( root ) {
		if ( ! root ) {
			return;
		}
		const variant = root.getAttribute( "data-variant" ) || "";
		const track = root.querySelector( ".rlcship-carousel__track" );
		if ( ! track ) {
			return;
		}

		let state = root.__rlcshipCarousel;
		if ( ! state ) {
			state = {
				track,
				dotsEl: null,
				autoplayTimer: null,
				paused: false,
				raf: null,
				children: 0,
				slides: [],
				targets: [],
				activeIndex: 0,
				originalCount: 0,
				cloneCount: 0,
				loopClonesEnabled: false,
				settleTimer: null,
				_ignoreMutations: false,
				align: variant === "testimonials" ? "center" : "start",
				resizeObserver: null,
				windowResizeHandler: null,
				editorMode: false,
			};
			root.__rlcshipCarousel = state;
		}

		// Track can be replaced by editor rerenders.
		if ( state.track !== track ) {
			if (
				state.resizeObserver &&
				typeof state.resizeObserver.disconnect === "function"
			) {
				state.resizeObserver.disconnect();
				state.resizeObserver = null;
			}
			state.track = track;
		}

		function readConfig() {
			let autoplayDelay = parseIntSafe(
				root.getAttribute( "data-autoplay-delay" ),
				5000
			);
			if ( autoplayDelay < 1000 ) {
				autoplayDelay = 1000;
			}
			if ( autoplayDelay > 60000 ) {
				autoplayDelay = 60000;
			}
			const baseCfg = {
				showNav: parseBool(
					root.getAttribute( "data-show-nav" ),
					true
				),
				showDots: parseBool(
					root.getAttribute( "data-show-dots" ),
					false
				),
				autoplay: parseBool(
					root.getAttribute( "data-autoplay" ),
					false
				),
				autoplayDelay,
				loop: parseBool( root.getAttribute( "data-loop" ), false ),
				pauseOnHover: parseBool(
					root.getAttribute( "data-pause-on-hover" ),
					false
				),
				editorMode: parseBool(
					root.getAttribute( "data-editor-mode" ),
					false
				),
				centerMode: variant === "testimonials",
				centerPadding: "0px",
			};

			// Testimonials should always loop (matches legacy Slick behaviour).
			if ( variant === "testimonials" ) {
				return Object.assign( {}, baseCfg, {
					loop: true,
				} );
			}

			if ( variant !== "awards" ) {
				return baseCfg;
			}

			// Awards variant: mirror the legacy Slick config from rlc-ship/src/block-slider.js.
			const w = window.innerWidth || 0;
			const slick = {
				slidesToShow: 5,
				centerMode: false,
				centerPadding: "0px",
				arrows: false,
				autoplay: false,
			};
			if ( w <= 1350 ) {
				Object.assign( slick, {
					slidesToShow: 4,
					centerMode: true,
					centerPadding: "5.5%",
					autoplay: true,
				} );
			}
			if ( w <= 1200 ) {
				Object.assign( slick, {
					slidesToShow: 3,
					centerMode: true,
					centerPadding: "11%",
					autoplay: true,
				} );
			}
			if ( w <= 1024 ) {
				Object.assign( slick, {
					slidesToShow: 3,
					centerMode: true,
					centerPadding: "9.5%",
					autoplay: true,
				} );
			}
			if ( w <= 992 ) {
				Object.assign( slick, {
					slidesToShow: 2,
					centerMode: true,
					centerPadding: "15%",
					autoplay: true,
				} );
			}
			if ( w <= 735 ) {
				Object.assign( slick, {
					slidesToShow: 1,
					centerMode: false,
					centerPadding: "0px",
					arrows: true,
					autoplay: true,
				} );
			}

			return Object.assign( {}, baseCfg, {
				showNav: slick.arrows,
				autoplay: slick.autoplay,
				// Slick's autoplaySpeed default is 3000ms.
				autoplayDelay: slick.autoplay ? 3000 : baseCfg.autoplayDelay,
				loop: true,
				pauseOnHover: true,
				centerMode: slick.centerMode,
				centerPadding: slick.centerMode ? slick.centerPadding : "0px",
			} );
		}

		function ensureLoopClones( cfg, forceRebuild ) {
			if ( ! state.track ) {
				state.slides = [];
				state.targets = [];
				state.originalCount = 0;
				state.cloneCount = 0;
				state.loopClonesEnabled = false;
				return;
			}

			const container = getSlideContainer( state.track );
			if ( ! container || ! container.children ) {
				state.slides = [];
				state.targets = [];
				state.originalCount = 0;
				state.cloneCount = 0;
				state.loopClonesEnabled = false;
				return;
			}

			const allSlides = getSlideElements( state.track );
			const originals = [];
			let existingClones = 0;
			for ( let i = 0; i < allSlides.length; i++ ) {
				const node = allSlides[ i ];
				if ( isCloneSlide( node ) ) {
					existingClones++;
					continue;
				}
				originals.push( node );
			}
			const originalCount = originals.length;
			const wantsLoopClones =
				!! cfg.loop && ! cfg.editorMode && originalCount >= 2;
			let cloneCount = 0;
			if ( wantsLoopClones ) {
				cloneCount = computeCloneCount( state.track, originals );
				// Keep DOM bloat reasonable; we only need enough to fill the viewport.
				cloneCount = clamp(
					cloneCount,
					1,
					Math.min( originalCount, 6 )
				);
			}

			const expectedClones = wantsLoopClones ? cloneCount * 2 : 0;
			const shouldRebuild =
				!! forceRebuild ||
				state.loopClonesEnabled !== wantsLoopClones ||
				state.cloneCount !== cloneCount ||
				existingClones !== expectedClones;

			if ( shouldRebuild ) {
				state._ignoreMutations = true;
				removeExistingClones( container );

				if ( wantsLoopClones && cloneCount > 0 && originalCount >= 2 ) {
					const firstOriginal = originals[ 0 ] || null;

					// Leading clones: last N originals (in order).
					const tailStart = Math.max( 0, originalCount - cloneCount );
					for ( let i = tailStart; i < originalCount; i++ ) {
						const original = originals[ i ];
						if ( ! original ) {
							continue;
						}
						const clone = original.cloneNode( true );
						stripCloneAttrsRecursive( clone );
						clone.setAttribute( CLONE_ATTR, "1" );
						clone.setAttribute( ORIGIN_ATTR, String( i ) );
						clone.setAttribute( "aria-hidden", "true" );
						clone.setAttribute( "tabindex", "-1" );
						container.insertBefore( clone, firstOriginal );
					}

					// Trailing clones: first N originals.
					for ( let i = 0; i < cloneCount; i++ ) {
						const original = originals[ i ];
						if ( ! original ) {
							continue;
						}
						const clone = original.cloneNode( true );
						stripCloneAttrsRecursive( clone );
						clone.setAttribute( CLONE_ATTR, "1" );
						clone.setAttribute( ORIGIN_ATTR, String( i ) );
						clone.setAttribute( "aria-hidden", "true" );
						clone.setAttribute( "tabindex", "-1" );
						container.appendChild( clone );
					}
				}
				// Delay re-enabling mutations to let pending MutationObserver callbacks clear.
				// This prevents infinite loops where clone creation triggers update() again.
				window.setTimeout( function () {
					state._ignoreMutations = false;
				}, 0 );
			}

			state.originalCount = originalCount;
			state.loopClonesEnabled = wantsLoopClones;
			state.cloneCount = wantsLoopClones ? cloneCount : 0;
			state.slides = getSlideElements( state.track );
			refreshTargets();

			if ( shouldRebuild && originalCount > 0 ) {
				const logical = clamp(
					typeof state.activeIndex === "number"
						? state.activeIndex
						: 0,
					0,
					Math.max( 0, originalCount - 1 )
				);
				state.activeIndex = logical;
				const physical = state.loopClonesEnabled
					? state.cloneCount + logical
					: logical;
				scrollToIndex(
					state.track,
					physical,
					state.targets,
					state.slides,
					"auto"
				);
			}
		}

		function getChildrenCount( forceRefresh, cfg ) {
			const effectiveCfg = cfg || readConfig();
			ensureLoopClones( effectiveCfg, !! forceRefresh );
			return state.originalCount || 0;
		}

		function refreshTargets() {
			if ( ! state.track ) {
				state.targets = [];
				return;
			}
			if ( ! state.slides || ! state.slides.length ) {
				state.slides = getSlideElements( state.track );
			}
			state.targets = computeSlideTargets(
				state.track,
				state.slides,
				state.align ||
					( variant === "testimonials" ? "center" : "start" )
			);
		}

		function physicalToLogicalIndex( physicalIndex ) {
			if ( ! state.loopClonesEnabled || ! state.cloneCount ) {
				return physicalIndex;
			}
			const slide = state.slides && state.slides[ physicalIndex ];
			if ( slide && isCloneSlide( slide ) ) {
				return clamp(
					parseIntSafe( slide.getAttribute( ORIGIN_ATTR ), 0 ),
					0,
					Math.max( 0, state.originalCount - 1 )
				);
			}
			return clamp(
				physicalIndex - state.cloneCount,
				0,
				Math.max( 0, state.originalCount - 1 )
			);
		}

		function logicalToPhysicalIndex( logicalIndex ) {
			return state.loopClonesEnabled
				? state.cloneCount + logicalIndex
				: logicalIndex;
		}

		function findBoundaryClone( originIndex, side ) {
			if (
				! state.loopClonesEnabled ||
				! state.cloneCount ||
				! state.slides
			) {
				return -1;
			}
			const want = String( originIndex );
			if ( side === "leading" ) {
				for ( let i = 0; i < state.cloneCount; i++ ) {
					const s = state.slides[ i ];
					if (
						s &&
						isCloneSlide( s ) &&
						s.getAttribute( ORIGIN_ATTR ) === want
					) {
						return i;
					}
				}
				return -1;
			}
			const start = state.cloneCount + state.originalCount;
			for ( let i = start; i < state.slides.length; i++ ) {
				const s = state.slides[ i ];
				if (
					s &&
					isCloneSlide( s ) &&
					s.getAttribute( ORIGIN_ATTR ) === want
				) {
					return i;
				}
			}
			return -1;
		}

		function scheduleLoopCorrection( children ) {
			if ( state.settleTimer ) {
				window.clearTimeout( state.settleTimer );
				state.settleTimer = null;
			}
			let attempts = 0;
			const maxAttempts = 10;
			const checkInterval = 50;

			function attemptCorrection() {
				state.settleTimer = null;
				if ( ! state.loopClonesEnabled || ! state.track ) {
					return;
				}
				if ( ! state.targets || ! state.targets.length ) {
					return;
				}
				const nearest = getNearestIndex(
					state.track.scrollLeft,
					state.targets
				);
				const slide = state.slides && state.slides[ nearest ];
				if ( ! slide || ! isCloneSlide( slide ) ) {
					return;
				}
				const snapDelta = Math.abs(
					state.track.scrollLeft - state.targets[ nearest ]
				);
				const maxScrollLeft = Math.max(
					0,
					( state.track.scrollWidth || 0 ) -
						( state.track.clientWidth || 0 )
				);
				const nearBoundary =
					state.track.scrollLeft <= 2 ||
					Math.abs( state.track.scrollLeft - maxScrollLeft ) <= 2;

				// If scroll hasn't settled yet, retry.
				// At hard boundaries (start/end), target positions can be clamped by the browser,
				// so snapDelta may never reach 0 even though we should still correct from clone -> real.
				if ( snapDelta > 3 && ! nearBoundary ) {
					attempts++;
					if ( attempts < maxAttempts ) {
						state.settleTimer = window.setTimeout(
							attemptCorrection,
							checkInterval
						);
					}
					return;
				}
				const origin = clamp(
					parseIntSafe( slide.getAttribute( ORIGIN_ATTR ), 0 ),
					0,
					Math.max( 0, children - 1 )
				);
				const physical = logicalToPhysicalIndex( origin );
				// Use instant scroll and block scroll events during correction
				state._correcting = true;
				scrollToIndex(
					state.track,
					physical,
					state.targets,
					state.slides,
					"instant"
				);
				state.activeIndex = origin;
				// Re-enable scroll events after a microtask
				window.setTimeout( function () {
					state._correcting = false;
				}, 0 );
			}

			// Initial delay before first check
			state.settleTimer = window.setTimeout( attemptCorrection, 100 );
		}

		function getActiveIndex( children ) {
			if ( ! children ) {
				return 0;
			}
			if ( ! state.targets || ! state.targets.length ) {
				refreshTargets();
			}
			if (
				typeof state.activeIndex === "number" &&
				state.activeIndex >= 0 &&
				state.activeIndex < children
			) {
				return state.activeIndex;
			}
			const physical = getNearestIndex(
				state.track.scrollLeft,
				state.targets
			);
			return physicalToLogicalIndex( physical );
		}

		function normalizeTarget( index, loop, children ) {
			if ( loop ) {
				return wrapIndex( index, children );
			}
			return clamp( index, 0, Math.max( 0, children - 1 ) );
		}

		function go( delta, loop, children ) {
			if ( ! children ) {
				return;
			}
			// If we're currently positioned on a clone, jump to the real slide first
			if ( state.loopClonesEnabled && state.targets && state.slides ) {
				const physicalPos = getNearestIndex(
					state.track.scrollLeft,
					state.targets
				);
				const currentSlide = state.slides[ physicalPos ];
				if ( currentSlide && isCloneSlide( currentSlide ) ) {
					const origin = parseIntSafe(
						currentSlide.getAttribute( ORIGIN_ATTR ),
						0
					);
					const realPhysical = logicalToPhysicalIndex( origin );
					// Instant jump to real slide before navigating
					state._correcting = true;
					instantScrollTo( state.track, state.targets[ realPhysical ] );
					state.activeIndex = origin;
					state._correcting = false;
				}
			}
			const current = getActiveIndex( children );
			const target = normalizeTarget( current + delta, loop, children );
			if ( state.loopClonesEnabled && loop && children >= 2 ) {
				if ( delta === 1 && current === children - 1 && target === 0 ) {
					const trailing = findBoundaryClone( 0, "trailing" );
					if ( trailing >= 0 ) {
						scrollToIndex(
							state.track,
							trailing,
							state.targets,
							state.slides
						);
					} else {
						scrollToIndex(
							state.track,
							logicalToPhysicalIndex( target ),
							state.targets,
							state.slides
						);
					}
				} else if (
					delta === -1 &&
					current === 0 &&
					target === children - 1
				) {
					const leading = findBoundaryClone(
						children - 1,
						"leading"
					);
					if ( leading >= 0 ) {
						scrollToIndex(
							state.track,
							leading,
							state.targets,
							state.slides
						);
					} else {
						scrollToIndex(
							state.track,
							logicalToPhysicalIndex( target ),
							state.targets,
							state.slides
						);
					}
				} else {
					scrollToIndex(
						state.track,
						logicalToPhysicalIndex( target ),
						state.targets,
						state.slides
					);
				}
			} else if ( loop && children >= 2 ) {
				// Loop enabled but clones not available (fallback).
				// Detect wrap-around: target jumped to opposite end.
				const isWrapping =
					( delta > 0 && target < current ) ||
					( delta < 0 && target > current );
				if ( isWrapping ) {
					// Instant jump to avoid visible backward scroll.
					const left = state.targets
						? state.targets[ logicalToPhysicalIndex( target ) ]
						: undefined;
					if ( typeof left === 'number' && isFinite( left ) ) {
						instantScrollTo( state.track, left );
					}
				} else {
					scrollToIndex(
						state.track,
						logicalToPhysicalIndex( target ),
						state.targets,
						state.slides
					);
				}
			} else {
				scrollToIndex(
					state.track,
					logicalToPhysicalIndex( target ),
					state.targets,
					state.slides
				);
			}
			state.activeIndex = target;
		}

		function goTo( index, loop, children ) {
			if ( ! children ) {
				return;
			}
			const target = normalizeTarget( index, loop, children );
			scrollToIndex(
				state.track,
				logicalToPhysicalIndex( target ),
				state.targets,
				state.slides
			);
			state.activeIndex = target;
		}

		function startAutoplay( cfg, children ) {
			if (
				! cfg.autoplay ||
				cfg.editorMode ||
				children < 2 ||
				state.autoplayTimer
			) {
				return;
			}
			state.autoplayTimer = window.setInterval( function () {
				if ( state.paused ) {
					return;
				}
				go( 1, cfg.loop, children );
			}, cfg.autoplayDelay );
		}

		function stopAutoplay() {
			if ( ! state.autoplayTimer ) {
				return;
			}
			window.clearInterval( state.autoplayTimer );
			state.autoplayTimer = null;
		}

		function setActiveSlideClass( index ) {
			if ( ! state.slides ) {
				return;
			}
			if (
				state.loopClonesEnabled &&
				state.cloneCount &&
				state.originalCount
			) {
				for ( let i = 0; i < state.slides.length; i++ ) {
					const slide = state.slides[ i ];
					if ( ! slide || ! slide.classList ) {
						continue;
					}
					if ( isCloneSlide( slide ) ) {
						slide.classList.remove( "is-active" );
						continue;
					}
					const logical = i - state.cloneCount;
					slide.classList.toggle( "is-active", logical === index );
				}
				return;
			}
			for ( let i = 0; i < state.slides.length; i++ ) {
				state.slides[ i ].classList.toggle( "is-active", i === index );
			}
		}

		function syncActiveState( cfg, children ) {
			if ( ! children ) {
				return;
			}
			if ( ! state.targets || ! state.targets.length ) {
				refreshTargets();
			}
			const physical = getNearestIndex(
				state.track.scrollLeft,
				state.targets
			);
			const active = normalizeTarget(
				physicalToLogicalIndex( physical ),
				cfg.loop,
				children
			);
			state.activeIndex = active;
			if ( state.dotsEl ) {
				setActiveDot( state.dotsEl, active );
			}
			setActiveSlideClass( active );
			if (
				state.loopClonesEnabled &&
				cfg.loop &&
				children >= 2 &&
				state.slides &&
				state.slides[ physical ] &&
				isCloneSlide( state.slides[ physical ] )
			) {
				scheduleLoopCorrection( children );
			}
		}

		function handleResize() {
			if ( ! state.track || state._handlingResize ) {
				return;
			}
			state._handlingResize = true;
			const cfg = readConfig();
			state.editorMode = cfg.editorMode;
			state.align = cfg.centerMode ? "center" : "start";
			if ( variant === "awards" ) {
				root.classList.toggle( "is-center-mode", !! cfg.centerMode );
				root.style.setProperty(
					"--rlcship-awards-center-padding",
					cfg.centerMode ? cfg.centerPadding : "0px"
				);
			}
			// Don't force-rebuild clones on resize - just refresh targets.
			// Clones only change when slides are added/removed.
			const children = getChildrenCount( false, cfg );
			if ( children < 1 ) {
				state._handlingResize = false;
				return;
			}
			let baseline =
				typeof state.activeIndex === "number"
					? state.activeIndex
					: physicalToLogicalIndex(
							getNearestIndex(
								state.track.scrollLeft,
								state.targets
							)
					  );
			if ( typeof state.activeIndex === "number" ) {
				baseline = normalizeTarget(
					state.activeIndex,
					cfg.loop,
					children
				);
			}
			scrollToIndex(
				state.track,
				logicalToPhysicalIndex( baseline ),
				state.targets,
				state.slides,
				"auto"
			);
			syncActiveState( cfg, children );
			state._handlingResize = false;
		}

		function bindResizeObserver() {
			if ( ! state.track ) {
				return;
			}
			if ( window.ResizeObserver ) {
				if ( ! state.resizeObserver ) {
					// Debounce resize to prevent infinite loops when clones change layout.
					let resizeRaf = null;
					state.resizeObserver = new window.ResizeObserver(
						function () {
							if ( resizeRaf ) {
								return;
							}
							resizeRaf = window.requestAnimationFrame( function () {
								resizeRaf = null;
								handleResize();
							} );
						}
					);
				}
				state.resizeObserver.observe( state.track );
			} else if ( ! state.windowResizeHandler ) {
				state.windowResizeHandler = function () {
					handleResize();
				};
				window.addEventListener( "resize", state.windowResizeHandler );
			}
		}

		function update() {
			const cfg = readConfig();
			state.editorMode = cfg.editorMode;
			state.align = cfg.centerMode ? "center" : "start";
			if ( variant === "awards" ) {
				root.classList.toggle( "is-center-mode", !! cfg.centerMode );
				root.style.setProperty(
					"--rlcship-awards-center-padding",
					cfg.centerMode ? cfg.centerPadding : "0px"
				);
			}
			const children = getChildrenCount( true, cfg );
			state.children = children;
			if (
				children > 0 &&
				( typeof state.activeIndex !== "number" ||
					state.activeIndex >= children ||
					state.activeIndex < 0 )
			) {
				state.activeIndex = 0;
			}
			if ( children === 0 ) {
				state.activeIndex = 0;
			}

			// Dots
			if ( cfg.showDots && children >= 2 ) {
				state.dotsEl = ensureDots( root );
				const dotsCount = state.dotsEl.querySelectorAll(
					".rlcship-carousel__dot"
				).length;
				if ( dotsCount !== children ) {
					renderDots( state.dotsEl, children );
				}
			} else if ( state.dotsEl ) {
				if ( state.dotsEl.parentNode ) {
					state.dotsEl.parentNode.removeChild( state.dotsEl );
				}
				state.dotsEl = null;
			}

			// Autoplay
			stopAutoplay();
			startAutoplay( cfg, children );

			syncActiveState( cfg, children );
		}

		if ( ! state.__eventsBound ) {
			state.__eventsBound = true;

			// Nav buttons (if present)
			root.addEventListener( "click", function ( evt ) {
				const t = evt.target;
				if ( ! t || ! t.classList ) {
					return;
				}
				const cfg = readConfig();
				const children = getChildrenCount( false, cfg );
				if ( children < 2 ) {
					return;
				}
				if ( t.classList.contains( "rlcship-carousel__prev" ) ) {
					go( -1, cfg.loop, children );
				}
				if ( t.classList.contains( "rlcship-carousel__next" ) ) {
					go( 1, cfg.loop, children );
				}
				if ( t.classList.contains( "rlcship-carousel__dot" ) ) {
					const i = parseIntSafe( t.getAttribute( "data-index" ), 0 );
					goTo( i, cfg.loop, children );
				}
			} );

			state.track.addEventListener( "scroll", function () {
				// Skip during loop correction to prevent state interference
				if ( state._correcting ) {
					return;
				}
				if ( state.raf ) {
					return;
				}
				state.raf = window.requestAnimationFrame( function () {
					state.raf = null;
					if ( state._correcting ) {
						return;
					}
					const cfg = readConfig();
					const children = getChildrenCount( false, cfg );
					if ( children < 1 ) {
						return;
					}
					syncActiveState( cfg, children );
				} );
			} );

			root.addEventListener( "focusin", function () {
				state.paused = true;
			} );
			root.addEventListener( "focusout", function () {
				state.paused = false;
			} );

			// Optional pause-on-hover behavior
			root.addEventListener( "mouseenter", function () {
				const cfg = readConfig();
				if ( cfg.pauseOnHover ) {
					state.paused = true;
				}
			} );
			root.addEventListener( "mouseleave", function () {
				const cfg = readConfig();
				if ( cfg.pauseOnHover ) {
					state.paused = false;
				}
			} );

			// Watch for slide count changes (editor) and attribute toggles.
			if ( window.MutationObserver ) {
				state.trackObserver = new window.MutationObserver( function () {
					if ( state._ignoreMutations ) {
						return;
					}
					update();
				} );
				state.trackObserver.observe( state.track, {
					childList: true,
					subtree: true,
				} );

				state.attrObserver = new window.MutationObserver( function () {
					if ( state._ignoreMutations ) {
						return;
					}
					update();
				} );
				state.attrObserver.observe( root, {
					attributes: true,
					attributeFilter: [
						"data-show-dots",
						"data-show-nav",
						"data-autoplay",
						"data-autoplay-delay",
						"data-loop",
						"data-pause-on-hover",
						"data-editor-mode",
					],
				} );
			}
		}

		update();
		bindResizeObserver();
	}

	function boot() {
		const carousels = document.querySelectorAll(
			"[data-rlcship-carousel]"
		);
		for ( let i = 0; i < carousels.length; i++ ) {
			initOrUpdateCarousel( carousels[ i ] );
		}

		// Editor can insert blocks after DOMContentLoaded.
		if ( window.MutationObserver && ! window.__rlcshipCarouselObserver ) {
			window.__rlcshipCarouselObserver = new window.MutationObserver(
				function ( mutations ) {
					for ( let m = 0; m < mutations.length; m++ ) {
						const added = mutations[ m ].addedNodes;
						if ( ! added ) {
							continue;
						}
						for ( let n = 0; n < added.length; n++ ) {
							const node = added[ n ];
							if ( ! node || node.nodeType !== 1 ) {
								continue;
							}
							if (
								node.matches &&
								node.matches( "[data-rlcship-carousel]" )
							) {
								initOrUpdateCarousel( node );
							}
							if ( node.querySelectorAll ) {
								const nested = node.querySelectorAll(
									"[data-rlcship-carousel]"
								);
								for ( let j = 0; j < nested.length; j++ ) {
									initOrUpdateCarousel( nested[ j ] );
								}
							}
						}
					}
				}
			);
			window.__rlcshipCarouselObserver.observe(
				document.documentElement,
				{
					childList: true,
					subtree: true,
				}
			);
		}
	}

	if ( document.readyState === "loading" ) {
		document.addEventListener( "DOMContentLoaded", boot );
	} else {
		boot();
	}
}
